import type { TAnalytics } from '@alfa-bank/site-helpers-common/src/browser/send-event/models';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect } from 'react';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import type { TCloseReason } from '@alfa-bank/site-mobile-bottom-sheet/src/models';

import type { IMobileAnalytics, TChangeCityHandler, TCloseHandler } from '../models';

const useDecoratedOpen = (open: boolean, analytics?: TAnalytics) => {
    useEffect(() => {
        if (open && analytics) sendEvent(analytics);
    }, [open, analytics]);
};

const useDecoratedClose = (
    onClose: TCloseHandler,
    closeAnalytics?: TAnalytics,
    swipeCloseAnalytics?: TAnalytics
) => {
    return useCallback<TCloseHandler>(
        reason => {
            const swipeReasons: TCloseReason[] = ['drag', 'click-control', 'click-overlay'];

            if (swipeCloseAnalytics && reason && swipeReasons.includes(reason)) {
                sendEvent(swipeCloseAnalytics);
            } /* istanbul ignore else */ else if (closeAnalytics) {
                sendEvent(closeAnalytics);
            }
            onClose();
        },
        [swipeCloseAnalytics, closeAnalytics, onClose]
    );
};

const useDecoratedChange = (
    onChange: TChangeCityHandler,
    analyticsFromTop?: TAnalytics,
    analyticsFromList?: TAnalytics
) => {
    return useCallback<TChangeCityHandler>(
        (city, fromTopList) => {
            if (fromTopList && analyticsFromTop) {
                sendEvent(analyticsFromTop);
            } else if (analyticsFromList) {
                sendEvent(analyticsFromList);
            }

            onChange(city);
        },
        [onChange, analyticsFromTop, analyticsFromList]
    );
};

const useDecoratedSearchHandler = (
    searchChanged: (text: string) => void,
    analytics?: TAnalytics
) => {
    return useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            if (analytics) {
                sendEvent(analytics);
            }
            searchChanged(event.target.value);
        },
        [analytics, searchChanged]
    );
};

interface IInputs {
    changeSearch: (text: string) => void;
    onChange: TChangeCityHandler;
    onClose: TCloseHandler;
    open: boolean;
}

export const useAnalytics = (inputs: IInputs, analyticsDict: IMobileAnalytics = {}) => {
    useDecoratedOpen(inputs.open, analyticsDict.modalOpen);

    return {
        searchHandler: useDecoratedSearchHandler(inputs.changeSearch, analyticsDict.searchChange),
        changeHandler: useDecoratedChange(
            inputs.onChange,
            analyticsDict.changeFromTop,
            analyticsDict.changeFromList
        ),
        closeHandler: useDecoratedClose(
            inputs.onClose,
            analyticsDict.modalClose,
            analyticsDict.modalCloseSwipe
        ),
    };
};
